/* eslint-disable react/jsx-wrap-multilines */
import dayjs from 'dayjs';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import RaceButton from '@/components/elements/Button/RaceButton';
import DateSelector from '@/components/elements/DateSelector';
import RaceProgramDetail from '@/components/layouts/Race/Singlerace/RaceProgramInfo/RaceProgramDetail';
import useClock from '@/hooks/date/useClock';
import { RaceProgram } from '@/lib/apollo/types/gen/graphql';
import { getVelodromeByCode } from '@/lib/race/velodrome';
import { raceSelector } from '@/selectors/race';
import { toHalfWidth } from '@/utility/utils';

import { Headline } from '../style';
import {
  CssGroup,
  DateSelectorWrapper,
  InfoWarningIcon,
  LinkWrapper,
  List,
  NoRaceText,
  ProgramName,
  RaceProgramInfoWrapper,
  RaceProgramListWrapper,
  StopHeldText,
} from './style';

interface RaceCardTemplateProps {
  date: dayjs.Dayjs;
  racePrograms: RaceProgram[];
  loading: boolean;
  getCommentCount: (raceId: string) => boolean;
}

const RaceCardTemplate: React.FC<RaceCardTemplateProps> = ({
  date,
  racePrograms,
  loading,
  getCommentCount,
}) => {
  const currentTime = useClock();

  const getRaceFlags = (
    rp: RaceProgram,
    raceNum: number,
  ): {
    isPast: boolean;
    isCanceled: boolean;
    isFuture: boolean;
  } => {
    const race = rp.races.find(r => r.raceNum === raceNum);
    if (!race) {
      return {
        isPast: false,
        isCanceled: false,
        isFuture: true,
      };
    }
    return {
      isPast:
        rp.isCanceled ||
        raceSelector(race).marginedOrderDeadline.isBefore(currentTime),
      isCanceled: rp.isCanceled || raceSelector(race).isCanceled,
      isFuture: false,
    };
  };

  return (
    <>
      <Headline>出走表</Headline>
      <DateSelectorWrapper>
        <DateSelector
          date={date}
          displayFormat="YYYY年M月D日"
          pathFormat="YYYY-MM-DD"
          unit="day"
        />
      </DateSelectorWrapper>
      <RaceProgramListWrapper isShow={racePrograms.length > 0}>
        <CssGroup component="ul">
          {racePrograms.map(rp => (
            <CSSTransition key={rp.id} timeout={200}>
              <List>
                <RaceProgramInfoWrapper>
                  <RaceProgramDetail
                    raceProgram={rp}
                    hasLink={!rp.isCanceled}
                    href={`/${
                      getVelodromeByCode(rp.velodromeCode).romanName
                    }/racecard/${rp.heldDate.format('YYYY-MM-DD')}`}
                  />
                  {rp.isCanceled && (
                    <StopHeldText>
                      <InfoWarningIcon />
                      レースは中止になりました
                    </StopHeldText>
                  )}
                  <ProgramName>{toHalfWidth(rp.programName)}</ProgramName>
                </RaceProgramInfoWrapper>
                <LinkWrapper>
                  {[...Array(rp.raceCount)].map((_, i) => (
                    <RaceButton
                      key={`race-${rp.id}-${i}`}
                      raceNum={i + 1}
                      to={`/${
                        getVelodromeByCode(rp.velodromeCode).romanName
                      }/race/${rp.heldDate.format('YYYY-MM-DD')}/${i + 1}`}
                      {...getRaceFlags(rp, i + 1)}
                      raceId={rp.races[i]?.id}
                      getCommentCount={getCommentCount}
                    />
                  ))}
                </LinkWrapper>
              </List>
            </CSSTransition>
          ))}
        </CssGroup>
      </RaceProgramListWrapper>
      <CSSTransition
        in={racePrograms.length === 0 && !loading}
        timeout={200}
        unmountOnExit
      >
        <NoRaceText>開催情報がありません</NoRaceText>
      </CSSTransition>
    </>
  );
};

export default RaceCardTemplate;
